/** Waterfall — cumulative bridge with absolute `total` anchors (the three-way margin
 *  bridge, OTIF Δ decomposition, order-book movement). `fmt` formats the on-bar value
 *  labels.
 *
 *  A bridge is read left to right, so the connectors matter as much as the bars: a
 *  dotted rule carries the eye from where one step ended to where the next begins.
 *  A floating step rounds both ends; an anchor sits square on the baseline. */
import React from 'react'
import { useTokens, type Tokens } from './useTokens'
import { useChartWidth } from './useChartWidth'
import { axisScale, barPath, barWidth, BAR_RADIUS } from './chartMarks'
import AxisRows from './AxisRows'

interface Step {
  label: string
  v: number
  type?: 'total'
}
interface Props {
  steps: Step[]
  posColor?: string
  fmt: (v: number) => string
}

const H = 236
const PAD = { l: 54, r: 16, t: 26, b: 38 }
const ROWS = 4

/**
 * Cumulative from/to per step; `total` steps anchor to the axis floor AND SEED the
 * running total. Seeding is what makes it a bridge: without it an opening anchor of
 * 3,800 was followed by a "+640 added" bar drawn from zero, so the movement bars
 * measured off a baseline the opening bar never established and the two halves of
 * the chart were on different quantities. A trailing total (the EBITDA headline)
 * seeds nothing that follows, so this is a no-op there.
 */
function compute(steps: Step[]): { from: number; to: number }[] {
  let cum = 0
  return steps.map((s) => {
    if (s.type === 'total') {
      cum = s.v
      return { from: 0, to: s.v }
    }
    const from = cum
    cum += s.v
    return { from, to: cum }
  })
}

/**
 * The axis a bridge is read on. A bridge from 90.5 to 94.2 plotted from zero crushes
 * every step between the anchors into an invisible sliver — the deltas ARE the chart,
 * so the axis covers the band the bridge actually moves through, with the floor
 * labelled so a shortened anchor bar can't be mistaken for the whole quantity. When
 * the bridge starts near zero anyway, zero stays the baseline.
 */
function domain(vals: { from: number; to: number }[], steps: Step[]): { lo: number; hi: number } {
  const marks = vals.flatMap((v, i) => (steps[i].type === 'total' ? [v.to] : [v.from, v.to]))
  const hi = Math.max(...marks, 0)
  const low = Math.min(...marks, hi)
  const span = hi - low || Math.abs(hi) || 1
  return { lo: low >= 0 && low <= span * 0.35 ? 0 : low - span * 0.3, hi }
}

function Bar({ s, ext, y, cx, bw, bottom, pos, fmt, t }: {
  s: Step; ext: { from: number; to: number }; y: (v: number) => number; cx: number
  bw: number; bottom: number; pos: string; fmt: (v: number) => string; t: Tokens
}): React.JSX.Element {
  const y1 = y(ext.to)
  const y0 = s.type === 'total' ? bottom : y(ext.from)
  const top = Math.min(y0, y1)
  const h = Math.max(3, Math.abs(y1 - y0))
  const colour = s.type === 'total' ? t.blue : s.v >= 0 ? pos : t.bad
  const label = (s.v >= 0 || s.type === 'total' ? '' : '−') + fmt(Math.abs(s.v))
  return (
    <g>
      {/* An anchor stands on the baseline (square foot, rounded cap); a floating step
          touches nothing, so both its ends round — a plain rounded rect. */}
      {s.type === 'total' ? (
        <path d={barPath(cx - bw / 2, top, bw, h)} fill={colour} opacity={0.92} />
      ) : (
        <rect x={cx - bw / 2} y={top} width={bw} height={h} rx={Math.min(BAR_RADIUS, bw / 2, h / 2)} fill={colour} opacity={0.92} />
      )}
      <text x={cx} y={H - 14} textAnchor="middle" fill={t.mut2} fontSize={9.5} fontFamily="JetBrains Mono">{s.label}</text>
      <text x={cx} y={top - 7} textAnchor="middle" fill={t.mut} fontSize={10} fontWeight={600} fontFamily="JetBrains Mono">{label}</text>
    </g>
  )
}

export default function Waterfall({ steps, posColor, fmt }: Props): React.JSX.Element {
  const t = useTokens()
  const [box, W] = useChartWidth()
  const vals = compute(steps)

  const plotW = Math.max(W - PAD.l - PAD.r, 40)
  const plotH = H - PAD.t - PAD.b
  const bottom = PAD.t + plotH
  const { lo, hi } = domain(vals, steps)
  const axis = axisScale(lo, hi, ROWS)
  const y = (v: number): number => bottom - plotH * ((v - axis.lo) / (axis.step * ROWS || 1))
  const step = plotW / Math.max(steps.length, 1)
  const bw = barWidth(step)
  const cx = (i: number): number => PAD.l + step * i + step / 2
  const pos = posColor || t.green

  return (
    <div ref={box} className="w-full min-w-0">
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: H, display: 'block' }} role="img" aria-label="Bridge from opening to closing">
        <AxisRows x1={PAD.l} x2={W - PAD.r} rows={ROWS} rowY={(r) => y(axis.lo + axis.step * r)}
          left={(r) => fmt(axis.lo + axis.step * r)} t={t} />
        {steps.map((s, i) =>
          i === 0 || s.type === 'total' ? null : (
            <line key={`c${i}`} x1={cx(i - 1) + bw / 2} x2={cx(i) - bw / 2} y1={y(vals[i].from)} y2={y(vals[i].from)}
              stroke={t.mut2} strokeWidth={1} strokeDasharray="2 3" opacity={0.7} />
          )
        )}
        {steps.map((s, i) => (
          <Bar key={i} s={s} ext={vals[i]} y={y} cx={cx(i)} bw={bw} bottom={bottom} pos={pos} fmt={fmt} t={t} />
        ))}
      </svg>
    </div>
  )
}
